import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import { useData } from "../lib/DataContext";
import { useAuth } from "../lib/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, LayoutTemplate, GitFork, Rocket } from "lucide-react";

export default function Templates() {
  return (
    <div className="p-6">
      <PageHeader title="Templates" subtitle="Reusable task bundles and multi-stage workflows" />
      <Tabs defaultValue="task">
        <TabsList className="mb-4">
          <TabsTrigger value="task" data-testid="tpl-tab-task">Task Templates</TabsTrigger>
          <TabsTrigger value="workflow" data-testid="tpl-tab-workflow">Workflow Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="task"><TemplateList kind="task" /></TabsContent>
        <TabsContent value="workflow"><TemplateList kind="workflow" /></TabsContent>
      </Tabs>
    </div>
  );
}

function TemplateList({ kind }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [inst, setInst] = useState(null);
  const canCreate = ["master_admin", "admin", "manager"].includes(user?.role);
  const load = () => api.get("/templates", { params: { kind } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [kind]);
  const del = async (id) => { await api.delete(`/templates/${id}`); load(); };

  return (
    <div>
      {canCreate && <div className="flex justify-end mb-3"><Button onClick={() => setShow(true)} data-testid={`new-${kind}-template`}><Plus size={16} className="mr-1" /> New {kind === "task" ? "Task" : "Workflow"} Template</Button></div>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((t) => (
          <div key={t.id} className="bg-white border rounded-xl p-5" data-testid={`template-${t.human_id}`}>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                {kind === "workflow" ? <GitFork size={18} className="text-indigo-600" /> : <LayoutTemplate size={18} className="text-indigo-600" />}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-slate-800">{t.name}</div>
                <div className="text-xs text-slate-400 font-mono">{t.human_id} · {t.items?.length || 0} tasks</div>
              </div>
              {canCreate && <button className="text-slate-400 hover:text-red-600" onClick={() => del(t.id)}><Trash2 size={15} /></button>}
            </div>
            {t.description && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{t.description}</p>}
            <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
              {t.items?.map((it, i) => (
                <div key={i} className="text-xs flex items-center gap-1.5 text-slate-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" />
                  {it.stage && <span className="text-[10px] bg-slate-100 rounded px-1">{it.stage}</span>}
                  {it.title} <span className="text-slate-400 ml-auto">+{it.offset_days}d/{it.duration_days}d</span>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => setInst(t)} data-testid={`instantiate-${t.human_id}`}>
              <Rocket size={14} className="mr-1" /> Use Template
            </Button>
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-full text-center py-12 text-slate-400 text-sm">No {kind} templates yet.</div>}
      </div>
      {show && <TemplateModal kind={kind} onClose={() => setShow(false)} onDone={() => { setShow(false); load(); }} />}
      {inst && <InstantiateModal tpl={inst} onClose={() => setInst(null)} onDone={() => setInst(null)} />}
    </div>
  );
}

const emptyItem = () => ({ title: "", stage: "", owner_id: "", offset_days: 0, duration_days: 1, priority: "Medium", depends_on: [] });

function TemplateModal({ kind, onClose, onDone }) {
  const { users, departments, projects } = useData();
  const [f, setF] = useState({ name: "", description: "", department_id: "", project_id: "", auto_chain: kind === "workflow" });
  const [items, setItems] = useState([emptyItem()]);
  const setItem = (i, k, v) => setItems((p) => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const submit = async () => {
    if (!f.name) return toast.error("Template name required");
    const valid = items.filter((i) => i.title.trim());
    if (valid.length === 0) return toast.error("Add at least one task");
    const payload = {
      name: f.name, kind, description: f.description, auto_chain: f.auto_chain,
      department_id: f.department_id || null, project_id: f.project_id || null,
      items: valid.map((i) => ({ ...i, owner_id: i.owner_id || null, offset_days: Number(i.offset_days), duration_days: Number(i.duration_days), stage: i.stage || null, depends_on: i.depends_on || [] })),
    };
    await api.post("/templates", payload);
    toast.success("Template created"); onDone();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-modal">
        <DialogHeader><DialogTitle>New {kind === "task" ? "Task" : "Workflow"} Template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Template Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={kind === "workflow" ? "e.g. New Project Launch" : "e.g. Employee Onboarding"} data-testid="template-name" /></div>
          <div><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          {kind === "workflow" && (
            <div className="flex items-center gap-2"><Switch checked={f.auto_chain} onCheckedChange={(v) => setF({ ...f, auto_chain: v })} data-testid="template-autochain" /><Label className="mb-0">Auto-link each stage to the previous stage</Label></div>
          )}
          <div>
            <Label>Tasks {kind === "workflow" ? "(group with stage names)" : ""}</Label>
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-1.5 mt-1.5 items-center">
                <Input className="col-span-4" placeholder="Task title" value={it.title} onChange={(e) => setItem(i, "title", e.target.value)} data-testid={`tpl-item-title-${i}`} />
                {kind === "workflow" && <Input className="col-span-2" placeholder="Stage" value={it.stage} onChange={(e) => setItem(i, "stage", e.target.value)} />}
                <div className={kind === "workflow" ? "col-span-2" : "col-span-3"}>
                  <Select value={it.owner_id} onValueChange={(v) => setItem(i, "owner_id", v)}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Owner" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input className="col-span-2" type="number" title="start offset (days)" value={it.offset_days} onChange={(e) => setItem(i, "offset_days", e.target.value)} />
                <Input className={kind === "workflow" ? "col-span-1" : "col-span-2"} type="number" title="duration (days)" value={it.duration_days} onChange={(e) => setItem(i, "duration_days", e.target.value)} />
                <button className="col-span-1 text-slate-400" onClick={() => setItems(items.filter((_, idx) => idx !== i))}><Trash2 size={14} /></button>
              </div>
            ))}
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => setItems([...items, emptyItem()])} data-testid="tpl-add-item">+ Add task</Button>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="template-submit">Create Template</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InstantiateModal({ tpl, onClose, onDone }) {
  const { users, departments, projects } = useData();
  const [f, setF] = useState({ start_date: new Date().toISOString().slice(0, 10), project_id: tpl.project_id || "", department_id: tpl.department_id || "", owner_id: "" });
  const submit = async () => {
    const payload = { start_date: f.start_date || null, project_id: f.project_id || null, department_id: f.department_id || null, owner_id: f.owner_id || null };
    const { data } = await api.post(`/templates/${tpl.id}/instantiate`, payload);
    toast.success(`${data.created} tasks created from "${tpl.name}"`); onDone();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="instantiate-modal">
        <DialogHeader><DialogTitle>Use Template — {tpl.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-500">This creates {tpl.items?.length || 0} tasks{tpl.kind === "workflow" ? " with stage dependencies" : ""}. Dates are offset from the start date.</p>
          <div><Label>Start Date</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="inst-start" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Project</Label>
              <Select value={f.project_id} onValueChange={(v) => setF({ ...f, project_id: v })}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Department</Label>
              <Select value={f.department_id} onValueChange={(v) => setF({ ...f, department_id: v })}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Default Owner (for unassigned tasks)</Label>
            <Select value={f.owner_id} onValueChange={(v) => setF({ ...f, owner_id: v })}>
              <SelectTrigger><SelectValue placeholder="Me" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="instantiate-submit">Create Tasks</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
